import React, { useState } from "react";
import { Button, Form, Card } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useDispatch, useSelector } from "react-redux";
import { ReduxState } from "../../../../redux";
import { handleAddNewKeyword } from "src/utils/categoryManagement";
import { createAlert } from "../../../../redux/reducers/Alerts";
import CreatableSelect from "react-select/creatable";
import { categorizeActionAsync } from "../../../../redux/reducers/CategoriesAsync";

type CreateRuleBannerProps = {
  transactionDescription: string;
  category: string;
  onDismiss: () => void;
  onCreateRule: () => void;
};

const generateOptions = (tagRegexes: any[], selectedTagIds: any[]) => {
  return selectedTagIds
    .map((t) => {
      const tag = tagRegexes.find((x) => x.id === t);
      return {
        label: `${tag?.tag}${tag?.tagLabel ? ` - ${tag.tagLabel}` : ""}`,
        value: tag?.tag || "",
      };
    })
    .sort((a, b) => a.label.localeCompare(b.label));
};

export const CreateRuleBanner: React.FC<CreateRuleBannerProps> = ({
  transactionDescription,
  category,
  onDismiss,
  onCreateRule,
}) => {
  const dispatch = useDispatch();
  const { tagRegexes, selectedTagRegexesSet, tagRegexesSets } = useSelector(
    (state: ReduxState) => state.TagRegexes
  );
  const { selectedL1: l1, selectedL2: l2 } = useSelector(
    (state: ReduxState) => state.TagLevel
  );

  const [keyword, setKeyword] = useState(transactionDescription);
  const [selectedCategory, setSelectedCategory] = useState(category);

  const selectedTagIds = !selectedTagRegexesSet
    ? []
    : tagRegexesSets
        .filter((t) => t.name === selectedTagRegexesSet.name)
        .map((x) => x.tagRegexesId);

  const options = generateOptions(tagRegexes, selectedTagIds);

  const handleCreateRule = () => {
    if (!keyword.trim() || !selectedCategory) {
      return;
    }

    const matchingTag = tagRegexes.find((t) => t.tag === selectedCategory);

    if (matchingTag) {
      handleAddNewKeyword(dispatch, tagRegexes, matchingTag.id, keyword, true);
      dispatch(
        createAlert({
          message:
            "Rule created. Running categorization to apply the new rule...",
          timeout: 5000,
          type: "success",
        })
      );
      // Automatically re-run categorization with the specific rule ID
      dispatch(categorizeActionAsync({ l1, l2, tagRegexIds: [matchingTag.id] }));
      onCreateRule();
    }
  };

  return (
    <div
      style={{
        position: "fixed",
        bottom: "20px",
        left: "50%",
        transform: "translateX(-50%)",
        zIndex: 1050,
        width: "90%",
        maxWidth: "900px",
      }}
    >
      <Card
        style={{
          boxShadow: "0 4px 20px rgba(0,0,0,0.2)",
          border: "2px solid #17a2b8",
        }}
      >
        <Card.Body className="p-3">
          <div className="d-flex justify-content-between align-items-start mb-3">
            <h5 className="mb-0">
              <FontAwesomeIcon icon="magic" className="mr-2 text-primary" />
              Create Categorization Rule
            </h5>
            <Button
              variant="link"
              size="sm"
              onClick={onDismiss}
              style={{ padding: "0", color: "#6c757d" }}
            >
              <FontAwesomeIcon icon="times" size="lg" />
            </Button>
          </div>
          <p className="mb-3 text-muted small">
            Create a rule to automatically categorize similar transactions in
            the future.
          </p>
          <div className="row">
            <div className="col-md-5 mb-2">
              <Form.Label className="font-weight-bold small mb-1">
                Keyword
              </Form.Label>
              <Form.Control
                type="text"
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                placeholder="Enter keyword..."
                size="sm"
              />
            </div>
            <div className="col-md-4 mb-2">
              <Form.Label className="font-weight-bold small mb-1">
                Category
              </Form.Label>
              <CreatableSelect
                placeholder="Select category..."
                options={options}
                value={
                  selectedCategory
                    ? { label: selectedCategory, value: selectedCategory }
                    : null
                }
                onChange={(selected) =>
                  setSelectedCategory(selected?.value || "")
                }
                isClearable
                styles={{
                  control: (base) => ({
                    ...base,
                    minHeight: "31px",
                    height: "31px",
                  }),
                  valueContainer: (base) => ({
                    ...base,
                    height: "31px",
                    padding: "0 6px",
                  }),
                  input: (base) => ({
                    ...base,
                    margin: "0",
                    padding: "0",
                  }),
                  indicatorsContainer: (base) => ({
                    ...base,
                    height: "31px",
                  }),
                }}
              />
            </div>
            <div className="col-md-3 mb-2 d-flex align-items-end">
              <Button
                variant="primary"
                onClick={handleCreateRule}
                size="sm"
                disabled={!keyword.trim() || !selectedCategory}
                block
              >
                <FontAwesomeIcon icon="plus" className="mr-1" />
                Create Rule
              </Button>
            </div>
          </div>
        </Card.Body>
      </Card>
    </div>
  );
};

export default CreateRuleBanner;
